import { type ReactNode, useState } from 'react';
import type { SheetView } from '../services/characterService.ts';
import { SheetActions, type SheetActionHandlers } from './SheetActions.tsx';
import { contentRegistry } from '@dcc/rules/content/dccCoreContent.ts';
import { VitalsHeader } from '../play/VitalsHeader.tsx';
import type { StatId } from '@dcc/rules/domain/stats.ts';
import { STAT_IDS, STAT_LABELS } from '@dcc/rules/domain/stats.ts';
import type { RollMode } from '@dcc/rules/rules/rollModes.ts';
import { RollModeContextMenu, type RollModeMenuState } from '../play/RollModeContextMenu.tsx';
import { planRollFor } from '@dcc/rules/app/rollService.ts';

const STATS = ['str', 'int', 'con', 'dex', 'cha'] as const;
const STAT_LABEL: Record<(typeof STATS)[number], string> = {
  str: 'STR', int: 'INT', con: 'CON', dex: 'DEX', cha: 'CHA',
};

function sign(n: number): string {
  return n >= 0 ? `+${n}` : `${n}`;
}

export interface SheetRollActions {
  mode: RollMode;
  disabled: boolean;
  rolling: string | null;
  onModeChange: (mode: RollMode) => void;
  onRollStat: (stat: StatId, mode?: RollMode) => void;
  onRollSkill: (skillId: string, stat: StatId, mode?: RollMode) => void;
}

/**
 * Crawler sheet. When `rolls` is supplied, the displayed Stats and Skills are
 * also the play controls: there is deliberately no second duplicate Roll tray.
 */
export function CharacterSheet({ view, actions, rolls, unskilled }: { view: SheetView; actions?: SheetActionHandlers; rolls?: SheetRollActions; unskilled?: ReactNode }) {
  const { character, effective, derived } = view;
  const [selectedSkillId, setSelectedSkillId] = useState<string | null>(null);
  const [selectedStat, setSelectedStat] = useState<StatId | null>(null);
  const [rollMenu, setRollMenu] = useState<(RollModeMenuState & ({ kind: 'stat'; stat: StatId } | { kind: 'skill'; skillId: string; stat: StatId })) | null>(null);
  const selectedSkill = selectedSkillId ? contentRegistry.getSkill(selectedSkillId) ?? null : null;
  const selectedPlan = selectedSkill && selectedStat
    ? planRollFor(effective, { stat: selectedStat, skill: selectedSkill })
    : null;

  function toggleSkill(skillId: string) {
    if (!rolls) return;
    if (selectedSkillId === skillId) {
      setSelectedSkillId(null);
      setSelectedStat(null);
      return;
    }
    setSelectedSkillId(skillId);
    // RULE DCC-CHK-006: never silently choose the Common Stat for the player.
    setSelectedStat(null);
  }

  return (
    <div className="dcc-sheet">
      <VitalsHeader character={character} derived={derived} />
      <header className="dcc-sheet-head">
        <h1>{character.name}</h1>
        <p className="dcc-subtitle">
          Level {character.level} · Floor {character.floor} · {character.crawlerType}
        </p>
      </header>

      {actions ? <SheetActions name={character.name} level={character.level} notes={character.notes} handlers={actions} disabled={rolls?.disabled} /> : null}

      <section className="dcc-vitals" aria-label="defences">
        <div className="dcc-vital">
          <span className="dcc-vital-label">Evade</span>
          <span className="dcc-vital-value">{sign(derived.evadeModifier)}</span>
        </div>
        <div className="dcc-vital">
          <span className="dcc-vital-label">Mitigation</span>
          <span className="dcc-vital-value">{derived.mitigation.damageResistance} DR</span>
        </div>
      </section>

      <section aria-label="stats">
        <div className="dcc-sheet-section-head">
          <h2>Stats</h2>
          {rolls && (
            <div className="dcc-sheet-roll-modes" aria-label="check roll mode">
              {(['normal', 'advantage', 'disadvantage'] as RollMode[]).map((mode) => (
                <button
                  type="button"
                  key={mode}
                  className={rolls.mode === mode ? 'dcc-mini-mode is-selected' : 'dcc-mini-mode'}
                  disabled={rolls.disabled}
                  onClick={() => rolls.onModeChange(mode)}
                >
                  {mode === 'normal' ? 'Normal' : mode === 'advantage' ? 'Advantage' : 'Disadvantage'}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="dcc-stat-grid">
          {STATS.map((s) => rolls ? (
            <button
              type="button"
              key={s}
              className="dcc-stat dcc-stat-roll"
              disabled={rolls.disabled}
              title={`Roll ${STAT_LABEL[s]} Check (${rolls.mode}). Right-click to choose roll mode.`}
              onClick={() => rolls.onRollStat(s)}
              onContextMenu={(event) => {
                event.preventDefault();
                setRollMenu({ x: event.clientX, y: event.clientY, label: `${STAT_LABEL[s]} Check`, kind: 'stat', stat: s });
              }}
            >
              <span className="dcc-stat-label">{STAT_LABEL[s]}</span>
              <span className="dcc-stat-score">{effective.enhancedStats[s]}</span>
              <span className="dcc-stat-mod">{sign(effective.statMod(s))}</span>
            </button>
          ) : (
            <div key={s} className="dcc-stat">
              <span className="dcc-stat-label">{STAT_LABEL[s]}</span>
              <span className="dcc-stat-score">{effective.enhancedStats[s]}</span>
              <span className="dcc-stat-mod">{sign(effective.statMod(s))}</span>
            </div>
          ))}
        </div>
        {rolls && <p className="dcc-creator-help">Click a Stat to roll it. The selected roll mode applies to Stat and Skill Checks.</p>}
      </section>

      <section aria-label="skills">
        <h2>Skills</h2>
        {character.skills.length === 0 ? (
          <p className="dcc-empty">No skills yet.</p>
        ) : (
          <ul className="dcc-skill-list">
            {character.skills.map((sk) => {
              const rank = effective.effectiveSkillRank(sk.skillId);
              const bonus = effective.skillRankBonus(sk.skillId);
              const skill = contentRegistry.getSkill(sk.skillId);
              const selected = rolls && selectedSkillId === sk.skillId;
              return (
                <li key={sk.skillId} className={selected ? 'dcc-skill is-selected' : 'dcc-skill'}>
                  {rolls ? (
                    <>
                      <button
                        type="button"
                        className="dcc-skill-roll"
                        disabled={rolls.disabled}
                        onClick={() => toggleSkill(sk.skillId)}
                      >
                        <span>{skill?.name ?? sk.skillId}</span>
                        <span className="dcc-skill-rank">Rank {rank}{bonus !== 0 ? ` (${sign(bonus)})` : ''}</span>
                      </button>
                      {selected && skill && (
                        <div className="dcc-inline-skill-roll">
                          <p className="dcc-creator-help">Choose the Stat that fits this Check.</p>
                          <div className="dcc-inline-stat-choices">
                            {STAT_IDS.map((stat) => (
                              <button
                                type="button"
                                key={stat}
                                className={selectedStat === stat ? 'dcc-mini-mode is-selected' : 'dcc-mini-mode'}
                                disabled={rolls.disabled}
                                onClick={() => setSelectedStat(stat)}
                              >
                                {STAT_LABELS[stat].abbr}{skill.commonStat === stat ? ' · common' : ''}
                              </button>
                            ))}
                          </div>
                          {selectedPlan && (
                            <>
                              <p className={selectedPlan.blockedReason ? 'dcc-creator-warn' : 'dcc-creator-help'}>
                                {selectedPlan.blockedReason ?? selectedPlan.formula}
                              </p>
                              <button
                                type="button"
                                disabled={rolls.disabled || !selectedPlan.ready || selectedStat === null}
                                onClick={() => selectedStat && rolls.onRollSkill(skill.id, selectedStat)}
                                onContextMenu={(event) => {
                                  if (selectedStat === null) return;
                                  event.preventDefault();
                                  setRollMenu({ x: event.clientX, y: event.clientY, label: `${skill.name} Check`, kind: 'skill', skillId: skill.id, stat: selectedStat });
                                }}
                                title="Right-click to roll straight, with Advantage, or with Disadvantage."
                              >
                                {rolls.rolling ?? `Roll ${skill.name}`}
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <span>{skill?.name ?? sk.skillId}</span>
                      <span className="dcc-skill-rank">Rank {rank}{bonus !== 0 ? ` (${sign(bonus)})` : ''}</span>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {unskilled}
      <section aria-label="inventory">
        <h2>Inventory</h2>
        {character.inventory.length === 0 ? (
          <p className="dcc-empty">Nothing carried.</p>
        ) : (
          <ul className="dcc-skill-list">
            {character.inventory.map((entry) => (
              <li key={entry.itemId} className="dcc-skill">
                <span>{contentRegistry.getItem(entry.itemId)?.name ?? entry.itemId}</span>
                <span className="dcc-skill-rank">× {entry.quantity}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
      {rolls && (
        <RollModeContextMenu
          menu={rollMenu}
          onClose={() => setRollMenu(null)}
          onChoose={(mode) => {
            const target = rollMenu;
            setRollMenu(null);
            if (!target) return;
            if (target.kind === 'stat') rolls.onRollStat(target.stat, mode);
            else rolls.onRollSkill(target.skillId, target.stat, mode);
          }}
        />
      )}
    </div>
  );
}
